import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../types/firebase';
import { GuestGroup, Guest } from '../types/interfaces';
import { groupNeedsBus } from '../utils/needsBus';
import AdminLayout from '../components/AdminLayout';
import Loading from '../components/Loading';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CategoryBreakdown {
  label: string;
  invited: number;
  responded: number;
  coming: number;
  notComing: number;
  pending: number;
}

interface OverallStats {
  totalInvited: number;
  totalComing: number;
  totalResponded: number;
  totalNotComing: number;
  totalPending: number;
  totalNeedingBus: number;
  totalGroups: number;
  respondedGroups: number;
}

// ─── Computation ──────────────────────────────────────────────────────────────

function computeOverall(groups: GuestGroup[]): OverallStats {
  let totalInvited = 0, totalComing = 0, totalResponded = 0, totalNeedingBus = 0;
  let respondedGroups = 0;

  groups.forEach(group => {
    let groupResponded = false;
    const groupBus = groupNeedsBus(group) === true;
    group.guests.forEach(guest => {
      totalInvited++;
      if (guest.coming !== undefined) {
        totalResponded++;
        groupResponded = true;
        if (guest.coming) {
          totalComing++;
          if (groupBus) totalNeedingBus++;
        }
      }
    });
    if (groupResponded) respondedGroups++;
  });

  return {
    totalInvited,
    totalComing,
    totalResponded,
    totalNotComing: totalResponded - totalComing,
    totalPending: totalInvited - totalResponded,
    totalNeedingBus,
    totalGroups: groups.length,
    respondedGroups,
  };
}

function breakdownByField(
  groups: GuestGroup[],
  getCategory: (guest: Guest, group: GuestGroup) => string,
): CategoryBreakdown[] {
  const map = new Map<string, CategoryBreakdown>();

  const getOrCreate = (label: string): CategoryBreakdown => {
    if (!map.has(label)) {
      map.set(label, { label, invited: 0, responded: 0, coming: 0, notComing: 0, pending: 0 });
    }
    return map.get(label)!;
  };

  groups.forEach(group => {
    group.guests.forEach(guest => {
      const cat = getCategory(guest, group) || 'Unknown';
      const entry = getOrCreate(cat);
      entry.invited++;
      if (guest.coming !== undefined) {
        entry.responded++;
        if (guest.coming) entry.coming++;
        else entry.notComing++;
      } else {
        entry.pending++;
      }
    });
  });

  return Array.from(map.values()).sort((a, b) => b.invited - a.invited);
}

// ─── Components ───────────────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: number | string; color?: string }> = ({ label, value, color = 'text-gray-800' }) => (
  <div className="bg-gray-50 p-4 rounded text-center">
    <h3 className="text-gray-500 text-xs uppercase tracking-wide mb-1">{label}</h3>
    <p className={`text-2xl font-bold m-0 ${color}`}>{value}</p>
  </div>
);

const ProgressBar: React.FC<{ coming: number; notComing: number; pending: number; total: number }> = ({ coming, notComing, pending, total }) => {
  if (total === 0) return null;
  const comingPct = (coming / total) * 100;
  const notComingPct = (notComing / total) * 100;
  const pendingPct = (pending / total) * 100;

  return (
    <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden flex">
      {comingPct > 0 && <div className="bg-emerald-500 h-full" style={{ width: `${comingPct}%` }} title={`Coming: ${coming}`} />}
      {notComingPct > 0 && <div className="bg-rose-400 h-full" style={{ width: `${notComingPct}%` }} title={`Not coming: ${notComing}`} />}
      {pendingPct > 0 && <div className="bg-gray-300 h-full" style={{ width: `${pendingPct}%` }} title={`Pending: ${pending}`} />}
    </div>
  );
};

const BreakdownSection: React.FC<{ title: string; data: CategoryBreakdown[] }> = ({ title, data }) => (
  <section className="bg-white p-6 rounded-lg shadow-md">
    <h2 className="text-gray-800 text-lg font-semibold m-0 mb-4">{title}</h2>
    <div className="space-y-4">
      {data.map(row => (
        <div key={row.label} className="border border-gray-100 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium text-gray-700">{row.label}</span>
            <span className="text-sm text-gray-500">{row.invited} invited</span>
          </div>
          <ProgressBar coming={row.coming} notComing={row.notComing} pending={row.pending} total={row.invited} />
          <div className="flex gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Coming: {row.coming}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-rose-400 inline-block" /> No: {row.notComing}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-gray-300 inline-block" /> Pending: {row.pending}
            </span>
          </div>
        </div>
      ))}
    </div>
  </section>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const Statistics: React.FC = () => {
  const [guestGroups, setGuestGroups] = useState<GuestGroup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'guestGroups'));
        const data = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as GuestGroup[];
        setGuestGroups(data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <section className="bg-white p-6 rounded-lg shadow-md">
          <Loading size="sm" />
        </section>
      </AdminLayout>
    );
  }

  const overall = computeOverall(guestGroups);

  const byLocation = breakdownByField(guestGroups, (g) => g.location || 'Unknown');
  const bySide = breakdownByField(guestGroups, (g) => g.side || 'Unknown');
  const byProbability = breakdownByField(guestGroups, (g) => g.probability || 'Unknown');
  const byAge = breakdownByField(guestGroups, (g) => {
    if (g.baby) return 'Baby';
    if (g.young) return 'Young';
    return 'Adult';
  });
  const byGender = breakdownByField(guestGroups, (g) => {
    if (g.gender === 'M') return 'Male';
    if (g.gender === 'F') return 'Female';
    return 'Unknown';
  });
  const responseRate = overall.totalInvited > 0
    ? Math.round((overall.totalResponded / overall.totalInvited) * 100)
    : 0;

  return (
    <AdminLayout>
      <div className="flex flex-col gap-6">
        <section className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-gray-800 text-lg font-semibold m-0">Overall</h2>
            <p className="text-gray-500 text-sm m-0">
              {overall.totalGroups} groups, {overall.totalInvited} people
            </p>
          </div>
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Response rate</span>
              <span className="text-sm font-medium text-gray-800">{responseRate}%</span>
            </div>
            <ProgressBar coming={overall.totalComing} notComing={overall.totalNotComing} pending={overall.totalPending} total={overall.totalInvited} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard label="Invited" value={overall.totalInvited} />
            <StatCard label="Coming" value={overall.totalComing} color="text-emerald-600" />
            <StatCard label="Not Coming" value={overall.totalNotComing} color="text-rose-500" />
            <StatCard label="Pending" value={overall.totalPending} color="text-amber-500" />
            <StatCard label="Responded" value={`${overall.respondedGroups}/${overall.totalGroups}`} />
            <StatCard label="Need Bus" value={overall.totalNeedingBus} />
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <BreakdownSection title="By Probability" data={byProbability} />
          <BreakdownSection title="By Location" data={byLocation} />
          <BreakdownSection title="By Side (Sara / Gavriel)" data={bySide} />
          <BreakdownSection title="By Age" data={byAge} />
          <BreakdownSection title="By Gender" data={byGender} />
        </div>
      </div>
    </AdminLayout>
  );
};

export default Statistics;
